<template>
  <div class="validation-history-tab">
    <!-- Filters Card -->
    <div class="filters-card">
      <div class="filters-row">
        <!-- Search by CDR ID -->
        <div class="filter-item filter-item--search">
          <v-icon class="search-icon" small>mdi-magnify</v-icon>
          <input
            v-model="filters.query"
            type="text"
            class="search-input"
            :placeholder="$t('cdr-dashboard.history.search-placeholder')"
            @keyup.enter="triggerSearch"
          />
        </div>

        <!-- Status Filter -->
        <select v-model="filters.status" class="filter-select" @change="triggerSearch">
          <option value="">{{ $t('cdr-dashboard.history.all-statuses') }}</option>
          <option value="valid">{{ $t('cdr-dashboard.history.status-valid') }}</option>
          <option value="valid_with_warnings">{{ $t('cdr-dashboard.history.status-valid-with-warnings') }}</option>
          <option value="blocked">{{ $t('cdr-dashboard.history.status-blocked') }}</option>
          <option value="auto_fixed">{{ $t('cdr-dashboard.history.status-auto-fixed') }}</option>
          <option value="correction_applied">{{ $t('cdr-dashboard.history.status-correction-applied') }}</option>
          <option value="cancelled">{{ $t('cdr-dashboard.history.status-cancelled') }}</option>
          <option value="auto_cancelled">{{ $t('cdr-dashboard.history.status-auto-cancelled') }}</option>
        </select>

        <!-- Source Filter -->
        <select v-model="filters.source" class="filter-select" @change="triggerSearch">
          <option value="">{{ $t('cdr-dashboard.history.source') }}</option>
          <option value="ocpp">OCPP</option>
          <option value="roaming">{{ $t('cdr-dashboard.history.source-roaming') }}</option>
          <option value="import">{{ $t('cdr-dashboard.history.source-import') }}</option>
        </select>

        <!-- Error Code Filter -->
        <select v-model="filters.errorCode" class="filter-select" @change="triggerSearch">
          <option value="">{{ $t('cdr-dashboard.history.error-code') }}</option>
          <option value="CDR_MISSING_TARIFF">{{ $t('cdr-dashboard.history.error-tariff') }}</option>
          <option value="CDR_METER_INVERSION">{{ $t('cdr-dashboard.history.error-meter') }}</option>
          <option value="CDR_METER_DISCONTINUITY">{{ $t('cdr-dashboard.history.error-discontinuity') }}</option>
          <option value="CDR_NEGATIVE_ENERGY">{{ $t('cdr-dashboard.history.error-negative') }}</option>
          <option value="CDR_SUSPICIOUS_DURATION">{{ $t('cdr-dashboard.history.error-suspicious-duration') }}</option>
          <option value="CDR_POWER_ANOMALY">{{ $t('cdr-dashboard.history.error-power-anomaly') }}</option>
        </select>

        <!-- Date Range with improved pickers -->
        <div class="date-range-container">
          <v-menu
            v-model="dateFromMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <input
                :value="formatDateDisplay(filters.dateFrom)"
                type="text"
                class="date-input"
                :placeholder="$t('cdr-dashboard.history.date-from')"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filters.dateFrom"
              no-title
              :first-day-of-week="1"
              @input="dateFromMenu = false"
            />
          </v-menu>
          <span class="date-separator">{{ $t('cdr-dashboard.history.to') }}</span>
          <v-menu
            v-model="dateToMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <input
                :value="formatDateDisplay(filters.dateTo)"
                type="text"
                class="date-input"
                :placeholder="$t('cdr-dashboard.history.date-to')"
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filters.dateTo"
              no-title
              :first-day-of-week="1"
              @input="dateToMenu = false"
            />
          </v-menu>
        </div>
      </div>

      <div class="filters-actions">
        <button class="reset-btn" @click="resetFilters" :title="$t('cdr-dashboard.history.reset-filters')">
          <v-icon small>mdi-filter-off</v-icon>
        </button>
        <button class="apply-btn" @click="triggerSearch">
          {{ $t('cdr-dashboard.history.apply-filters') }}
        </button>
      </div>
    </div>

    <!-- Data Table -->
    <div class="table-card">
      <div class="table-container">
        <table class="history-table">
          <thead>
            <tr>
              <th>{{ $t('cdr-dashboard.history.col-date') }}</th>
              <th>{{ $t('cdr-dashboard.history.col-cdr-id') }}</th>
              <th>EVSE ID / EVCO ID</th>
              <th>{{ $t('cdr-dashboard.history.col-source') }}</th>
              <th>{{ $t('cdr-dashboard.history.col-status') }}</th>
              <th>{{ $t('cdr-dashboard.history.col-decision') }}</th>
              <th class="text-center">{{ $t('cdr-dashboard.history.col-errors') }}</th>
              <th class="text-center">{{ $t('cdr-dashboard.history.col-corrections') }}</th>
              <th class="text-center">{{ $t('cdr-dashboard.history.col-revisions') }}</th>
              <th class="text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="10" class="loading-cell">
                <v-progress-circular indeterminate color="primary" size="24" />
              </td>
            </tr>
            <tr v-else-if="items.length === 0">
              <td colspan="10" class="empty-cell">
                {{ $t('cdr-dashboard.history.no-results') }}
              </td>
            </tr>
            <tr v-else v-for="item in items" :key="item.id" @click="openDetail(item)" class="data-row">
              <!-- Date -->
              <td class="date-cell">{{ formatDate(item.validatedAt) }}</td>

              <!-- CDR ID -->
              <td class="cdr-id-cell">
                <a href="#" @click.prevent="openDetail(item)" class="cdr-link">
                  {{ formatCdrId(item.cdrId) }}
                </a>
              </td>

              <!-- EVSE ID / EVCO ID -->
              <td class="evse-cell">
                <template v-if="item.cdrSnapshot">
                  <a v-if="item.cdrSnapshot.evseId" href="#" class="id-link" @click.stop.prevent="applyQuery(item.cdrSnapshot.evseId)">{{ item.cdrSnapshot.evseId }}</a>
                  <span v-if="item.cdrSnapshot.evseId && item.cdrSnapshot.evcoId" class="id-separator"> / </span>
                  <a v-if="item.cdrSnapshot.evcoId" href="#" class="id-link evco" @click.stop.prevent="applyQuery(item.cdrSnapshot.evcoId)">{{ item.cdrSnapshot.evcoId }}</a>
                </template>
                <span v-else>-</span>
              </td>

              <!-- Source -->
              <td>
                <span class="source-badge">{{ formatSource(item.source) }}</span>
              </td>

              <!-- Status Global -->
              <td>
                <div class="status-cell">
                  <span class="status-badge" :class="getStatusClass(item.status)">
                    <span class="status-dot"></span>
                    {{ getStatusLabel(item.status) }}
                    <v-icon
                      v-if="item.status === 'valid_with_warnings'"
                      small
                      class="warning-icon"
                    >mdi-alert</v-icon>
                  </span>
                  <span class="status-subtitle">{{ getStatusSubtitle(item) }}</span>
                </div>
              </td>

              <!-- Decision Finale -->
              <td class="decision-cell">{{ getDecisionText(item) }}</td>

              <!-- Nb Erreurs / Warnings -->
              <td class="text-center">
                <span class="error-count" :class="getErrorCountClass(item)">
                  {{ item.issueCount || item.issues?.length || 0 }}
                </span>
              </td>

              <!-- Corrections -->
              <td class="text-center">
                <span v-if="item.correctionsCount > 0" class="correction-badge" :class="getCorrectionClass(item)">
                  {{ item.correctionsCount }} {{ $t('cdr-dashboard.history.correction') }}
                </span>
                <span v-else class="no-value">-</span>
              </td>

              <!-- Revisions -->
              <td class="text-center">
                <span v-if="item.revisionsCount > 0" class="revision-badge">
                  {{ item.revisionsCount }} {{ $tc('cdr-dashboard.history.revision', item.revisionsCount) }}
                </span>
                <span v-else class="no-value">-</span>
              </td>

              <!-- Action -->
              <td class="text-right">
                <button class="view-btn" @click.stop="openDetail(item)">
                  <v-icon small>mdi-eye</v-icon>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="table-footer">
        <span class="pagination-info">
          {{ $t('cdr-dashboard.history.showing', { start: paginationFrom, end: paginationTo, total: total }) }}
        </span>
        <div class="pagination-buttons">
          <button
            class="pagination-btn"
            :disabled="options.page <= 1"
            @click="prevPage"
          >
            <v-icon small>mdi-chevron-left</v-icon>
          </button>
          <button
            class="pagination-btn"
            :disabled="options.page >= totalPages"
            @click="nextPage"
          >
            <v-icon small>mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="900" scrollable>
      <v-card v-if="selectedItem">
        <v-card-title class="dialog-header">
          <div class="dialog-title-content">
            <v-icon left>mdi-file-document-check-outline</v-icon>
            <span>{{ $t('cdr-dashboard.history.detail-title') }}</span>
            <span class="status-badge ml-3" :class="getStatusClass(selectedItem.status)">
              <span class="status-dot"></span>
              {{ getStatusLabel(selectedItem.status) }}
            </span>
          </div>
          <v-btn icon @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider />

        <v-card-text class="dialog-content">
          <!-- Summary -->
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">{{ $t('cdr-dashboard.history.col-cdr-id') }}</span>
              <span class="detail-value font-mono">{{ selectedItem.cdrId || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('cdr-dashboard.history.col-date') }}</span>
              <span class="detail-value">{{ formatDate(selectedItem.validatedAt) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('cdr-dashboard.history.col-source') }}</span>
              <span class="source-badge">{{ formatSource(selectedItem.source) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">{{ $t('cdr-dashboard.history.col-decision') }}</span>
              <span class="detail-value">{{ getDecisionText(selectedItem) }}</span>
            </div>
          </div>

          <!-- Issues -->
          <div v-if="selectedItem.issues && selectedItem.issues.length > 0" class="detail-section">
            <h4 class="detail-section-title">{{ $t('cdr-dashboard.history.issues') }}</h4>
            <div class="issues-list">
              <div v-for="(issue, idx) in selectedItem.issues" :key="idx" class="issue-item" :class="issue.severity === 'blocking' ? 'issue-item--error' : 'issue-item--warning'">
                <div class="issue-code">{{ issue.code }}</div>
                <div class="issue-message" v-if="issue.message">{{ issue.message }}</div>
                <div class="issue-field" v-if="issue.field">{{ $t('cdr-dashboard.history.field') }}: {{ issue.field }}</div>
              </div>
            </div>
          </div>

          <!-- Auto Corrections -->
          <div v-if="selectedItem.autoCorrections && selectedItem.autoCorrections.length > 0" class="detail-section">
            <h4 class="detail-section-title">{{ $t('cdr-dashboard.history.auto-corrections') }}</h4>
            <table class="corrections-table">
              <thead>
                <tr>
                  <th>{{ $t('cdr-dashboard.history.field') }}</th>
                  <th>{{ $t('cdr-dashboard.history.old-value') }}</th>
                  <th>{{ $t('cdr-dashboard.history.new-value') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(corr, idx) in selectedItem.autoCorrections" :key="idx">
                  <td>{{ corr.field }}</td>
                  <td class="text-error">{{ corr.oldValue || corr.before }}</td>
                  <td class="text-success">{{ corr.newValue || corr.after }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-btn
            color="primary"
            outlined
            @click="goToCdrDetail(selectedItem)"
            small
          >
            <v-icon left small>mdi-open-in-new</v-icon>
            {{ $t('cdr-dashboard.history.view-full-detail') }}
          </v-btn>
          <v-spacer />
          <v-btn text @click="detailDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PendingCdrRepository from '@repository/PendingCdrRepository'
import dayjs from 'dayjs'

export default {
  name: 'ValidationHistoryTab',

  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      filters: {
        query: '',
        status: '',
        source: '',
        errorCode: '',
        dateFrom: '',
        dateTo: '',
      },
      options: {
        page: 1,
        itemsPerPage: 15,
      },
      detailDialog: false,
      selectedItem: null,
      dateFromMenu: false,
      dateToMenu: false,
    }
  },

  computed: {
    paginationFrom() {
      return ((this.options.page - 1) * this.options.itemsPerPage) + 1
    },

    paginationTo() {
      const to = this.options.page * this.options.itemsPerPage
      return to > this.total ? this.total : to
    },

    totalPages() {
      return Math.ceil(this.total / this.options.itemsPerPage)
    },
  },

  mounted() {
    this.searchData()
  },

  methods: {
    applyQuery(value) {
      this.filters.query = value
      this.triggerSearch()
    },

    triggerSearch() {
      this.options.page = 1
      this.searchData()
    },

    resetFilters() {
      this.filters = {
        query: '',
        status: '',
        source: '',
        errorCode: '',
        dateFrom: '',
        dateTo: '',
      }
      this.triggerSearch()
    },

    formatDateDisplay(date) {
      if (!date) return ''
      return dayjs(date).format(this.$t('format_date'))
    },

    prevPage() {
      if (this.options.page > 1) {
        this.options.page--
        this.searchData()
      }
    },

    nextPage() {
      if (this.options.page < this.totalPages) {
        this.options.page++
        this.searchData()
      }
    },

    async searchData() {
      this.loading = true
      try {
        const params = {
          offset: (this.options.page - 1) * this.options.itemsPerPage,
          limit: this.options.itemsPerPage,
          orderBy: 'validatedAt',
          orderDirection: 'desc',
        }

        if (this.filters.query) {
          params.query = this.filters.query
        }
        if (this.filters.status) {
          params.statuses = [this.filters.status]
        }
        if (this.filters.source) {
          if (this.filters.source === 'roaming') {
            params.sources = ['gireve_push', 'gireve_pull', 'hubject_push', 'hubject_pull']
          } else if (this.filters.source === 'import') {
            params.sources = ['manual_import']
          } else {
            params.sources = [this.filters.source]
          }
        }
        if (this.filters.errorCode) {
          params.issueCodes = [this.filters.errorCode]
        }
        if (this.filters.dateFrom) {
          params.dateFrom = this.filters.dateFrom
        }
        if (this.filters.dateTo) {
          params.dateTo = this.filters.dateTo
        }

        const result = await PendingCdrRepository.searchValidationLogs(params)
        this.items = result.items || []
        this.total = result.meta?.total || result.total || 0
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },

    applyFilters(query) {
      // Reset all filters first to avoid accumulation
      this.filters = {
        query: '',
        status: '',
        source: '',
        errorCode: '',
        dateFrom: '',
        dateTo: '',
      }

      // Then apply only the filters from query
      if (query.query) this.filters.query = query.query
      if (query.status) this.filters.status = query.status
      if (query.source) this.filters.source = query.source
      if (query.errorCode) this.filters.errorCode = query.errorCode
      if (query.dateFrom) this.filters.dateFrom = query.dateFrom
      if (query.dateTo) this.filters.dateTo = query.dateTo
      this.triggerSearch()
    },

    openDetail(item) {
      this.selectedItem = item
      this.detailDialog = true
    },

    goToCdrDetail(item) {
      if (!item?.cdrId) return
      this.detailDialog = false
      const route = this.$router.resolve({
        name: 'cdr-detail',
        params: { id: item.cdrId },
      })
      window.open(route.href, '_blank')
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return dayjs(dateStr).format('DD MMM YYYY, HH:mm')
    },

    formatCdrId(id) {
      if (!id) return '-'
      if (id.length > 16) {
        return `${id.substring(0, 4)}...${id.substring(id.length - 4)}`
      }
      return id
    },

    formatSource(source) {
      const map = {
        'ocpp': 'OCPP',
        'gireve_push': 'Roaming',
        'gireve_pull': 'Roaming',
        'hubject_push': 'Roaming',
        'hubject_pull': 'Roaming',
        'manual_import': 'Import',
      }
      return map[source?.toLowerCase()] || source || '-'
    },

    getStatusClass(status) {
      const map = {
        'valid': 'status-badge--success',
        'valid_with_warnings': 'status-badge--warning',
        'auto_fixed': 'status-badge--info',
        'blocked': 'status-badge--danger',
        'cancelled': 'status-badge--neutral',
        'auto_cancelled': 'status-badge--neutral',
        'duplicate': 'status-badge--neutral',
        'correction_applied': 'status-badge--corrected',
      }
      return map[status?.toLowerCase()] || 'status-badge--neutral'
    },

    getStatusLabel(status) {
      const map = {
        'valid': this.$t('cdr-dashboard.history.status-valid'),
        'valid_with_warnings': this.$t('cdr-dashboard.history.status-valid'),
        'auto_fixed': this.$t('cdr-dashboard.history.status-auto-correct'),
        'blocked': this.$t('cdr-dashboard.history.status-blocked'),
        'cancelled': this.$t('cdr-dashboard.history.status-cancelled'),
        'auto_cancelled': this.$t('cdr-dashboard.history.status-auto-cancelled'),
        'duplicate': this.$t('cdr-dashboard.history.status-cancelled'),
        'correction_applied': this.$t('cdr-dashboard.history.status-correction-applied'),
      }
      return map[status?.toLowerCase()] || status
    },

    getStatusSubtitle(item) {
      const status = item.status?.toLowerCase()

      if (status === 'valid_with_warnings') {
        // Show warning codes
        const codes = item.issueCodes || (item.issues?.map(i => i.code) || [])
        if (codes.length > 0) {
          return codes.map(c => this.getErrorLabel(c)).join(', ')
        }
        return this.$t('cdr-dashboard.history.with-warnings')
      }

      if (status === 'valid') {
        if (item.correctionsCount > 0 || item.autoCorrectionsCount > 0) {
          return this.$t('cdr-dashboard.history.after-correction')
        }
        return this.$t('cdr-dashboard.history.automatic')
      }

      if (status === 'blocked') {
        const codes = item.issueCodes || (item.issues?.map(i => i.code) || [])
        if (codes.length > 0) {
          return this.getErrorLabel(codes[0])
        }
        return ''
      }

      if (status === 'auto_fixed') {
        if (item.hasAutoCorrections || item.autoCorrectionsCount > 0) {
          return this.$t('cdr-dashboard.history.index-rebuilt')
        }
        return ''
      }

      if (status === 'auto_cancelled') {
        return this.$t('cdr-dashboard.history.automatic')
      }

      if (status === 'cancelled' || status === 'duplicate') {
        return this.$t('cdr-dashboard.history.duplicate')
      }

      return ''
    },

    getDecisionText(item) {
      const status = item.status?.toLowerCase()

      if (status === 'valid_with_warnings') {
        const codes = item.issueCodes || (item.issues?.map(i => i.code) || [])
        if (codes.length > 0) {
          return `${this.$t('cdr-dashboard.history.decision-valid-with-warnings')} (${codes.map(c => this.getErrorLabel(c)).join(', ')})`
        }
        return this.$t('cdr-dashboard.history.decision-valid-with-warnings')
      }

      if (status === 'valid' || status === 'correction_applied') {
        if (item.correctionsCount > 0 || item.autoCorrectionsCount > 0) {
          return this.$t('cdr-dashboard.history.decision-valid-after-correction')
        }
        return this.$t('cdr-dashboard.history.decision-conform')
      }

      if (status === 'blocked') {
        const codes = item.issueCodes || (item.issues?.map(i => i.code) || [])
        if (codes.length > 0) {
          return `${this.$t('cdr-dashboard.history.decision-reject')} - ${this.getErrorLabel(codes[0])}`
        }
        return this.$t('cdr-dashboard.history.decision-reject')
      }

      if (status === 'auto_fixed') {
        return this.$t('cdr-dashboard.history.decision-corrected')
      }

      if (status === 'auto_cancelled') {
        return this.$t('cdr-dashboard.history.decision-auto-cancelled')
      }

      if (status === 'cancelled' || status === 'duplicate') {
        return `${this.$t('cdr-dashboard.history.decision-cancelled')} - ${this.$t('cdr-dashboard.history.duplicate')}`
      }

      return '-'
    },

    getErrorLabel(code) {
      const map = {
        'CDR_MISSING_TARIFF': this.$t('cdr-dashboard.history.error-tariff'),
        'CDR_METER_INVERSION': this.$t('cdr-dashboard.history.error-meter'),
        'CDR_METER_DISCONTINUITY': this.$t('cdr-dashboard.history.error-discontinuity'),
        'CDR_NEGATIVE_ENERGY': this.$t('cdr-dashboard.history.error-negative'),
        'CDR_SUSPICIOUS_DURATION': this.$t('cdr-dashboard.history.error-suspicious-duration'),
        'CDR_POWER_ANOMALY': this.$t('cdr-dashboard.history.error-power-anomaly'),
      }
      return map[code] || code
    },

    getErrorCountClass(item) {
      const count = item.issueCount || item.issues?.length || 0
      if (count === 0) return 'error-count--zero'
      const status = item.status?.toLowerCase()
      if (status === 'blocked') return 'error-count--danger'
      if (status === 'valid_with_warnings') return 'error-count--warning'
      if (status === 'auto_fixed') return 'error-count--info'
      return 'error-count--warning'
    },

    getCorrectionClass(item) {
      if (item.status === 'auto_fixed') return 'correction-badge--info'
      return 'correction-badge--warning'
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #EA4335;
$primary-dark: #DC2626;
$success: #059669;
$danger: #DC2626;
$warning: #D97706;
$info: #3B82F6;
$corrected: #7C3AED;
$border-color: #E5E7EB;
$text-main: #111827;
$text-secondary: #6B7280;
$bg-light: #F9FAFB;
$bg-subtle: #F3F4F6;

.validation-history-tab {
  padding: 24px;
}

// Filters Card
.filters-card {
  background: white;
  border-radius: 12px;
  border: 1px solid $border-color;
  padding: 16px;
  margin-bottom: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  flex: 1;
}

.filter-item--search {
  position: relative;
  width: 240px;

  .search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #9CA3AF;
  }

  .search-input {
    width: 100%;
    padding: 8px 12px 8px 36px;
    border: 1px solid $border-color;
    border-radius: 8px;
    font-size: 13px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: $primary;
    }

    &::placeholder {
      color: #9CA3AF;
    }
  }
}

.filter-select {
  padding: 8px 32px 8px 12px;
  border: 1px solid $border-color;
  border-radius: 8px;
  font-size: 13px;
  background: white;
  cursor: pointer;
  outline: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;

  &:focus {
    border-color: $primary;
  }
}

.date-range-container {
  display: flex;
  align-items: center;
  border: 1px solid $border-color;
  border-radius: 8px;
  overflow: hidden;

  .date-input {
    padding: 8px 12px;
    border: none;
    font-size: 13px;
    outline: none;
    color: $text-secondary;
    cursor: pointer;
    min-width: 100px;
    background: transparent;

    &:focus {
      background: $bg-light;
    }

    &::placeholder {
      color: #9CA3AF;
    }
  }

  .date-separator {
    padding: 8px;
    background: $bg-subtle;
    font-size: 11px;
    font-weight: 700;
    color: #9CA3AF;
  }
}

.filters-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.reset-btn {
  padding: 8px;
  border: none;
  background: none;
  color: $text-secondary;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: $bg-subtle;
    color: $text-main;
  }
}

.apply-btn {
  padding: 8px 16px;
  background: $primary;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: $primary-dark;
  }
}

// Table Card
.table-card {
  background: white;
  border-radius: 12px;
  border: 1px solid $border-color;
  overflow: hidden;
}

.table-container {
  overflow-x: auto;
}

.history-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: $bg-light;

    th {
      padding: 12px 16px;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: $text-secondary;
      border-bottom: 1px solid $border-color;
      text-align: left;
      white-space: nowrap;
    }
  }

  tbody {
    .data-row {
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: $bg-light;
      }

      td {
        padding: 12px 16px;
        font-size: 13px;
        border-bottom: 1px solid rgba($border-color, 0.5);
        vertical-align: top;
      }
    }

    .loading-cell,
    .empty-cell {
      text-align: center;
      padding: 48px 16px;
      color: $text-secondary;
    }
  }
}

.date-cell {
  color: $text-secondary;
  white-space: nowrap;
}

.evse-cell {
  font-size: 11px;
  white-space: nowrap;
  .id-link {
    color: $primary;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
    &.evco {
      color: #6B7280;
    }
  }
  .id-separator {
    color: #D1D5DB;
  }
}

.cdr-id-cell {
  .cdr-link {
    font-family: ui-monospace, monospace;
    font-weight: 600;
    color: $primary;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: $primary-dark;
      text-decoration: underline;
    }
  }
}

.source-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background: $bg-subtle;
  border: 1px solid $border-color;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  color: $text-secondary;
}

.status-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  width: fit-content;

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  &--success {
    background: rgba($success, 0.1);
    color: $success;
    border: 1px solid rgba($success, 0.2);
    .status-dot { background: $success; }
  }

  &--danger {
    background: rgba($danger, 0.1);
    color: $danger;
    border: 1px solid rgba($danger, 0.2);
    .status-dot { background: $danger; }
  }

  &--info {
    background: rgba($info, 0.1);
    color: $info;
    border: 1px solid rgba($info, 0.2);
    .status-dot { background: $info; }
  }

  &--neutral {
    background: $bg-subtle;
    color: $text-secondary;
    border: 1px solid $border-color;
    .status-dot { background: #9CA3AF; }
  }

  &--warning {
    background: rgba($warning, 0.1);
    color: $warning;
    border: 1px solid rgba($warning, 0.2);
    .status-dot { background: $success; }

    .warning-icon {
      color: $warning;
      margin-left: 2px;
    }
  }

  &--corrected {
    background: rgba($corrected, 0.1);
    color: $corrected;
    border: 1px solid rgba($corrected, 0.2);
    .status-dot { background: $corrected; }
  }
}

.status-subtitle {
  font-size: 10px;
  color: $text-secondary;
  padding-left: 4px;
}

.decision-cell {
  color: $text-secondary;
  font-size: 12px;
}

.error-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;

  &--zero {
    background: $bg-subtle;
    color: #9CA3AF;
  }

  &--danger {
    background: rgba($danger, 0.1);
    color: $danger;
  }

  &--info {
    background: rgba($info, 0.1);
    color: $info;
  }

  &--warning {
    background: rgba($warning, 0.1);
    color: $warning;
  }
}

.correction-badge,
.revision-badge {
  display: inline-flex;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}

.correction-badge {
  &--info {
    background: rgba($info, 0.1);
    color: $info;
    border: 1px solid rgba($info, 0.2);
  }

  &--warning {
    background: rgba($warning, 0.1);
    color: $warning;
    border: 1px solid rgba($warning, 0.2);
  }
}

.revision-badge {
  background: $bg-light;
  color: $text-secondary;
  border: 1px solid $border-color;
}

.no-value {
  color: #9CA3AF;
}

.view-btn {
  padding: 6px;
  border: none;
  background: none;
  color: #9CA3AF;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba($primary, 0.1);
    color: $primary;
  }
}

// Pagination
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: $bg-light;
  border-top: 1px solid $border-color;
}

.pagination-info {
  font-size: 12px;
  color: $text-secondary;
}

.pagination-buttons {
  display: flex;
  gap: 8px;
}

.pagination-btn {
  padding: 8px;
  border: 1px solid $border-color;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: $bg-light;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Dialog
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
}

.dialog-title-content {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
}

.dialog-content {
  padding: 24px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-label {
  font-size: 11px;
  font-weight: 500;
  color: $text-secondary;
  text-transform: uppercase;
}

.detail-value {
  font-size: 14px;
  color: $text-main;
}

.font-mono {
  font-family: ui-monospace, monospace;
}

.detail-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid $border-color;
}

.detail-section-title {
  font-size: 13px;
  font-weight: 700;
  color: $text-main;
  margin-bottom: 12px;
}

.issues-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.issue-item {
  padding: 12px;
  border-radius: 8px;

  &--error {
    background: rgba($danger, 0.05);
    border-left: 3px solid $danger;
  }

  &--warning {
    background: rgba($warning, 0.05);
    border-left: 3px solid $warning;
  }

  .issue-code {
    font-weight: 700;
    font-size: 13px;
  }

  .issue-message,
  .issue-field {
    font-size: 12px;
    color: $text-secondary;
    margin-top: 4px;
  }
}

.corrections-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 8px 12px;
    text-align: left;
    font-size: 12px;
    border-bottom: 1px solid $border-color;
  }

  th {
    background: $bg-light;
    font-weight: 600;
    color: $text-secondary;
  }

  .text-error { color: $danger; }
  .text-success { color: $success; }
}

.text-center { text-align: center; }
.text-right { text-align: right; }
</style>
